import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  MessageCircle,
  Sparkles,
  ArrowRight,
  LockIcon,
  ShieldCheck,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import api from '@/utils/ky';
import { Dialog, Transition } from '@headlessui/react';
import { Input } from '@/components/ui/input';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { useCoachStore } from '@/store/coach';

const features = [
  {
    icon: Calendar,
    title: 'Automatic Meeting Analysis',
    description:
      'Automatically analyze your Google Meet 1:1s to uncover patterns and improvement opportunities in your management style.',
  },
  {
    icon: MessageCircle,
    title: 'Communication Insights',
    description:
      'Get detailed feedback on your communication effectiveness, questioning techniques, and listening skills during 1:1s.',
  },
  {
    icon: Sparkles,
    title: 'AI-Powered Recommendations',
    description:
      'Receive personalized recommendations to improve your leadership skills based on actual conversations with your team.',
  },
];

export default function MeetingLanding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isNameDialogOpen, setNameDialogOpen] = useState(false);
  const [isCalendarAuthDialogOpen, setCalendarAuthDialogOpen] = useState(false);
  const coachStore = useCoachStore();

  const [userName, setUserName] = useState(coachStore.userName || '');

  useEffect(() => {
    const error = searchParams.get('error');
    if (error === 'auth_failed') {
      toast.error('Failed to authenticate with Google. Please try again.');
    }
  }, [searchParams]);

  // Create user mutation
  const { mutate: createUser } = useMutation({
    mutationFn: (data: {
      name: string;
      deviceId: string;
      function: string;
    }) =>
      api
        .post('api/coach/users', {
          json: data,
        })
        .json<{ userId: string; deviceId: string }>(),
    onSuccess: async (data, variables) => {
      // Store user data
      coachStore.setUser({
        userId: data.userId,
        deviceId: data.deviceId,
        name: variables.name,
        function: variables.function,
      });

      setNameDialogOpen(false);

      // Get auth URL with the userId
      try {
        const { authUrl }: any = await api
          .get('api/meeting/auth-url', {
            searchParams: {
              userId: data.userId,
            },
          })
          .json();

        setAuthUrl(authUrl);
        setCalendarAuthDialogOpen(true);
      } catch (error) {
        console.error('Failed to get auth URL:', error);
        toast.error('Failed to setup calendar access. Please try again.');
      }
    },
    onError: (error) => {
      console.error('Failed to create user:', error);
      toast.error('Failed to register. Please try again.');
    },
  });

  const [authUrl, setAuthUrl] = useState<string | null>(null);

  const handleGetStarted = async () => {
    // Check if user already exists
    if (coachStore.userId) {
      try {
        // Try to get auth URL directly
        const { authUrl }: any = await api
          .get('api/meeting/auth-url', {
            searchParams: {
              userId: coachStore.userId,
            },
          })
          .json();

        setAuthUrl(authUrl);
        setCalendarAuthDialogOpen(true);
      } catch (error) {
        console.error('Failed to get auth URL:', error);
        toast.error('Failed to setup calendar access. Please try again.');
      }
    } else {
      setNameDialogOpen(true);
    }
  };

  const handleNameSubmit = () => {
    if (!userName.trim()) return;

    createUser({
      name: userName,
      deviceId: uuidv4(),
      function: 'other',
    });
  };

  const handleCalendarAuth = () => {
    if (authUrl) {
      // Set loading state in store
      coachStore.setGoogleAuth({ isAuthorized: false });
      window.location.href = authUrl;
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -right-1/2 w-[1000px] h-[1000px] bg-gradient-to-b from-primary/5 to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/2 w-[800px] h-[800px] bg-gradient-to-t from-blue-50/30 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="relative px-6 pt-20 lg:pt-32 lg:px-8 mb-32">
        <div className="mx-auto max-w-5xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-6xl lg:text-7xl font-bold tracking-tight bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent mb-8">
              Unlock Insights from
              <br />
              Your 1:1 Meetings
            </h1>

            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-12">
              Get AI-powered feedback on your management style and improve your
              leadership skills using your Google Meet transcripts.
            </p>

            <Button
              onClick={handleGetStarted}
              size="lg"
              className="px-8 py-6 text-lg bg-primary hover:bg-primary-600 rounded-full transition-all duration-200 hover:scale-105 shadow-lg"
            >
              Get Started
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>

          <div className="mt-20 grid md:grid-cols-3 gap-12">
            {features.map((feature) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl p-6 shadow-sm"
              >
                <feature.icon className="h-12 w-12 text-primary mb-4" />
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Name Dialog */}
      <Dialog
        open={isNameDialogOpen}
        onClose={() => setNameDialogOpen(false)}
        className="relative z-50"
      >
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur"
          aria-hidden="true"
        />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-sm rounded-lg bg-white p-6">
            <Dialog.Title className="text-lg font-medium mb-4">
              Let's get started
            </Dialog.Title>

            <Input
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              placeholder="Enter your name"
              className="mb-4"
            />

            <Button
              onClick={handleNameSubmit}
              disabled={!userName.trim()}
              className="w-full"
            >
              Continue
            </Button>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Calendar Auth Dialog */}
      <Dialog
        open={isCalendarAuthDialogOpen}
        onClose={() => setCalendarAuthDialogOpen(false)}
        className="relative z-50"
      >
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur"
          aria-hidden="true"
        />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md rounded-lg bg-white p-6">
            <div className="mb-6">
              <div className="flex items-center justify-center mb-4">
                <ShieldCheck className="h-12 w-12 text-green-500" />
              </div>

              <Dialog.Title className="text-xl font-medium text-center mb-2">
                Secure Calendar Access
              </Dialog.Title>

              <p className="text-gray-600 text-center mb-4">
                We need access to your Google Calendar to analyze your 1:1
                meetings.
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-start">
                  <LockIcon className="h-5 w-5 text-blue-500 mt-0.5 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900">
                      Your data is safe with us
                    </h4>
                    <ul className="mt-2 text-sm text-blue-700 space-y-1">
                      <li>• SOC2 Type II certified</li>
                      <li>• Data encrypted in transit and at rest</li>
                      <li>• Access limited to meeting transcripts only</li>
                      <li>• No data stored without your consent</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <Button
              onClick={handleCalendarAuth}
              className="w-full bg-primary hover:bg-primary-600"
            >
              Allow Calendar Access
              <Calendar className="ml-2 h-4 w-4" />
            </Button>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
}
